import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import config from '@/config';
import { LetterPartyGame } from '@/features/letterParty';
import { TracingGame } from '@/features/tracingGame';
import { clearGameState, finishGameRequest, getGameDataRequest } from '../store/actions';
import { JuniorGameType } from '../store/constants';
import { createJuniorSessionResult } from '../api/juniorSessionResult';

import GameSession from './GameSession';

import type { TracingGameData } from '@/features/tracingGame';
import type { GameFinishEvent } from '../types';
import type { GameWrapperProps } from './GameWrapper';

const JuniorGameWrapper = ({ onFinish, ...props }: GameWrapperProps) => {
	const dispatch = useDispatch();
	const [ready, setReady] = useState(false);
	const startedAt = useRef<number | null>(null);
	const finished = useRef(false);

	const handleStart = useCallback(() => {
		startedAt.current = performance.now();
		finished.current = false;
	}, []);

	useEffect(() => {
		dispatch(getGameDataRequest());
		setReady(true);

		return () => {
			dispatch(clearGameState());
		};
	}, [dispatch]);

	const handleFinish = useCallback(
		(event: GameFinishEvent, onFinished?: () => void) => {
			if (startedAt.current === null || finished.current) return;
			finished.current = true;
			const result = createJuniorSessionResult(
				event.gameOutcome,
				(performance.now() - startedAt.current) / 1000,
				event.correctRatio ?? 0,
			);
			dispatch(finishGameRequest(result, onFinished));
			onFinish?.(event);
		},
		[dispatch, onFinish],
	);

	const handleClose = useCallback(() => {
		if (config.exit_url) {
			window.location.href = config.exit_url;
		}
	}, []);

	if (!ready) return null;

	return (
		<GameSession {...props} onFinish={handleFinish} onClose={handleClose}>
			{(gameProps) => {
				switch (gameProps.gameData.game?.type) {
					case JuniorGameType.LETTER_PARTY:
						return <LetterPartyGame {...gameProps} showBestScore={false} onStart={handleStart} />;
					case JuniorGameType.TRACING:
						return (
							<TracingGame
								{...gameProps}
								onStart={handleStart}
								gameData={gameProps.gameData as unknown as TracingGameData}
							/>
						);
					default:
						return null;
				}
			}}
		</GameSession>
	);
};

export default JuniorGameWrapper;
